import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { promptTemplateService, PromptTemplate, CreateTemplateRequest, UpdateTemplateRequest } from '../services/promptTemplateService';

interface TemplateFormData {
  title: string;
  prompt: string;
  description: string;
  category: string;
}

const CATEGORIES = ['general', 'analysis', 'report', 'legal', 'underwriting', 'workflow'];

const PromptTemplates: React.FC = () => {
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<PromptTemplate | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [showInactive, setShowInactive] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(true);

  const [formData, setFormData] = useState<TemplateFormData>({
    title: '',
    prompt: '',
    description: '',
    category: 'general',
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadTemplates();
  }, [user, navigate]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await promptTemplateService.getUserTemplates({
        includeGlobal: false,
        isActive: !showInactive,
        sortBy: 'createdAt',
        sortOrder: 'DESC',
      });
      // This page manages the user's personal templates only. Company-wide and
      // global templates are managed in the Admin pages and surfaced via the
      // slash picker — keep them out of this list to avoid confusion.
      setTemplates(response.templates.filter((t) => (t.scope ?? 'personal') === 'personal'));
    } catch (err) {
      setError('Failed to load templates');
      console.error('Error loading templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    try {
      setError(null);
      const created = await promptTemplateService.createTemplate(formData);
      // Flip out of edit mode immediately so the detail view appears without
      // waiting for the sidebar list to refresh.
      setIsCreating(false);
      if (created) {
        setSelectedTemplate(created);
      }
      resetForm();
      void loadTemplates();
    } catch (err: any) {
      setError(err.message || 'Failed to create template');
    }
  };

  const handleUpdateTemplate = async () => {
    if (!selectedTemplate) return;
    try {
      setError(null);
      const updated = await promptTemplateService.updateTemplate(selectedTemplate.id, formData);
      setIsEditing(false);
      if (updated) {
        setSelectedTemplate(updated);
      }
      resetForm();
      void loadTemplates();
    } catch (err: any) {
      setError(err.message || 'Failed to update template');
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      setError(null);
      await promptTemplateService.deleteTemplate(templateId);
      await loadTemplates();
      if (selectedTemplate?.id === templateId) {
        setSelectedTemplate(null);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete template');
    }
  };

  const resetForm = () => {
    setFormData({ title: '', prompt: '', description: '', category: 'general' });
  };

  const startEdit = (template: PromptTemplate) => {
    setSelectedTemplate(template);
    setFormData({
      title: template.title,
      prompt: template.prompt || '',
      description: template.description || '',
      category: template.category,
    });
    setIsEditing(true);
    setIsCreating(false);
  };

  const startCreate = () => {
    resetForm();
    setIsCreating(true);
    setIsEditing(false);
    setSelectedTemplate(null);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setIsCreating(false);
    if (!selectedTemplate) {
      resetForm();
    }
  };

  const filteredTemplates = templates.filter((template) => {
    if (
      searchQuery &&
      !template.title.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !template.description?.toLowerCase().includes(searchQuery.toLowerCase())
    ) {
      return false;
    }
    if (selectedCategory && template.category !== selectedCategory) {
      return false;
    }
    return true;
  });

  const editing = isCreating || isEditing;

  return (
    <div className={`min-h-screen w-full ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold mb-1">Prompt Templates</h1>
            <p className="text-gray-600 dark:text-gray-400">
              Reusable prompts you can drop into chat with a slash command.
            </p>
          </div>
          <button
            onClick={startCreate}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap"
          >
            + New Template
          </button>
        </div>

        {/* How it works */}
        {showHowItWorks && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6 relative">
            <button
              onClick={() => setShowHowItWorks(false)}
              className="absolute top-2 right-2 text-blue-400 hover:text-blue-600 dark:hover:text-blue-300"
              aria-label="Dismiss"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">How prompt templates work</h3>
            <ul className="text-sm text-blue-900/80 dark:text-blue-200/80 space-y-1 list-disc list-inside">
              <li>Save prompts you reuse — analysis instructions, report formats, evaluation rubrics, etc.</li>
              <li>In any chat, type <code className="px-1 rounded bg-white/60 dark:bg-blue-950/60 font-mono">/</code> to open the picker, then select a template by title.</li>
              <li>The template's prompt is sent to the AI; only the title is shown and saved with your chat.</li>
              <li>Templates are personal by default. Company admins can publish company-wide templates.</li>
            </ul>
          </div>
        )}

        {/* Filter bar */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">All Categories</option>
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </option>
              ))}
            </select>
            <label className="flex items-center gap-2 px-2">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => {
                  setShowInactive(e.target.checked);
                  loadTemplates();
                }}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm">Show inactive</span>
            </label>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Main layout: slim list on left, big editor on right */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Template list */}
          <aside className="lg:col-span-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Your Templates ({filteredTemplates.length})
                </h2>
              </div>
              {loading ? (
                <div className="p-8 text-center">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                  <p className="mt-2 text-sm text-gray-500">Loading...</p>
                </div>
              ) : filteredTemplates.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <p className="text-sm">No templates yet</p>
                  <button
                    onClick={startCreate}
                    className="mt-3 text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    Create your first template
                  </button>
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[70vh] overflow-y-auto">
                  {filteredTemplates.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => {
                        setSelectedTemplate(template);
                        setIsEditing(false);
                        setIsCreating(false);
                      }}
                      className={`w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                        selectedTemplate?.id === template.id && !editing
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500'
                          : 'border-l-4 border-l-transparent'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-sm truncate">{template.title}</h3>
                          {template.description && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                              {template.description}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-1.5 text-[11px] text-gray-500">
                            <span className="bg-gray-100 dark:bg-gray-700 dark:text-gray-300 px-1.5 py-0.5 rounded">
                              {template.category}
                            </span>
                            <span>{template.usageCount} uses</span>
                            {!template.isActive && (
                              <span className="text-red-600 dark:text-red-400">Inactive</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </aside>

          {/* Focal area: editor / details */}
          <main className="lg:col-span-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              {editing ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    isCreating ? handleCreateTemplate() : handleUpdateTemplate();
                  }}
                  className="p-6 space-y-6"
                >
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                      Title
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Give your template a short, descriptive name"
                      className="w-full px-4 py-3 text-xl font-semibold border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      required
                      maxLength={100}
                      autoFocus
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="When should this template be used? (shown in the slash picker)"
                      className="w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      rows={3}
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Prompt
                      </label>
                      <span className="text-xs text-gray-400">
                        {formData.prompt.length.toLocaleString()} / 10,000
                      </span>
                    </div>
                    <textarea
                      value={formData.prompt}
                      onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                      placeholder="Write the prompt that will be sent to the AI when this template is selected..."
                      className="w-full px-4 py-3 text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white font-mono leading-relaxed"
                      rows={24}
                      required
                      maxLength={10000}
                    />
                  </div>

                  <div className="flex items-center gap-4 pt-2">
                    <div className="flex-1">
                      <label className="block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                        Category
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      >
                        {CATEGORIES.map((cat) => (
                          <option key={cat} value={cat}>
                            {cat.charAt(0).toUpperCase() + cat.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex gap-2 self-end">
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="px-5 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                      >
                        {isCreating ? 'Create Template' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                </form>
              ) : selectedTemplate ? (
                <div className="p-6 space-y-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <h2 className="text-2xl font-bold mb-2">{selectedTemplate.title}</h2>
                      {selectedTemplate.description && (
                        <p className="text-gray-600 dark:text-gray-400">{selectedTemplate.description}</p>
                      )}
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => startEdit(selectedTemplate)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(selectedTemplate.id)}
                        className="px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors text-sm font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <span className="bg-gray-100 dark:bg-gray-700 dark:text-gray-300 px-3 py-1 rounded-full text-xs font-medium">
                      {selectedTemplate.category}
                    </span>
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-full text-xs font-medium">
                      {selectedTemplate.usageCount} uses
                    </span>
                    {!selectedTemplate.isActive && (
                      <span className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-3 py-1 rounded-full text-xs font-medium">
                        Inactive
                      </span>
                    )}
                  </div>

                  <div>
                    <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                      Prompt
                    </h3>
                    <div className="bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <pre className="whitespace-pre-wrap text-sm font-mono leading-relaxed">
                        {selectedTemplate.prompt}
                      </pre>
                    </div>
                  </div>

                  <div className="text-xs text-gray-500 pt-2 border-t border-gray-100 dark:border-gray-800">
                    Created {new Date(selectedTemplate.createdAt).toLocaleDateString()} · Updated{' '}
                    {new Date(selectedTemplate.updatedAt).toLocaleDateString()}
                  </div>
                </div>
              ) : (
                <div className="p-12 text-center text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="font-medium mb-1">No template selected</p>
                  <p className="text-sm">Pick a template from the left, or create a new one.</p>
                  <button
                    onClick={startCreate}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    + New Template
                  </button>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default PromptTemplates;
